/**
 * @file LegalGroupCard.tsx
 * @description Состав группы документов (phase19.16, SB141; uiux-legal-docs
 * §4, uiux-legal-steps-b-c §6.4): ручная группа — список с перетаскиванием,
 * «+ Добавить документ в группу», × «убрать из группы»; автоматическая
 * (сертификаты производителей) — пояснение, как собирается состав.
 *
 * У групп нет редакций (решение §7.27 (в) 1): состав меняется сразу — каждое
 * действие сразу отправляет весь упорядоченный список (`PUT /:id/members`),
 * и карточка берётся из ответа. Документ бывает только в одной группе:
 * перенос из другой группы — после подтверждения (`move: true`), в том числе
 * когда сервер ответил 409 `legal_in_group_already`.
 */

import type { LegalDocumentDto, LegalGroupMemberDto, LegalInGroupAlreadyDetails } from "@chaiberi/shared";
import { useState } from "react";

import { ConfirmDialog } from "../ui/ConfirmDialog";
import { useNotify } from "../ui/NotifyProvider";
import { useConfirm } from "../ui/use-confirm";
import { ApiError } from "../../lib/api-client";
import { removeLegalGroupMember, setLegalGroupMembers, type LegalDocumentListItemDto } from "../../lib/api-legal";
import { legalErrorCode } from "../../lib/legal/legal-errors";
import { LegalAddMemberField } from "./LegalAddMemberField";
import { LegalCertificatesGroupBlock } from "./LegalCertificatesGroupBlock";
import { LegalGroupMembers } from "./LegalGroupMembers";

interface Props {
  doc: LegalDocumentDto;
  /** Карточка из ответа API. */
  onDoc: (doc: LegalDocumentDto) => void;
  /** Показать ошибку API. */
  fail: (err: unknown, fallback: string) => void;
}

/** Последний видимый документ опубликованной группы — без него группа пропадёт с сайта. */
function isLastVisible(doc: LegalDocumentDto, member: LegalGroupMemberDto): boolean {
  const visible = (doc.members ?? []).filter((m) => m.visibleOnSite);
  return doc.isPublished && member.visibleOnSite && visible.length === 1;
}

export function LegalGroupCard({ doc, onDoc, fail }: Props) {
  const notify = useNotify();
  const { confirm, request, settle } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const members = doc.members ?? [];

  /** `work` вернул null — человек передумал (перенос не подтвердил). */
  async function run(work: () => Promise<LegalDocumentDto | null>, fallback: string, done?: string) {
    setBusy(true);
    try {
      const next = await work();
      if (!next) return;
      onDoc(next);
      setRefreshKey((k) => k + 1);
      if (done) notify.success(done);
    } catch (err) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  }

  const askMove = (title: string, groupTitle: string) =>
    confirm({
      title: `Перенести «${title}» в эту группу?`,
      description: `Документ сейчас в группе «${groupTitle}». Документ бывает только в одной группе — оттуда он уйдёт.`,
      action: "Перенести",
    });

  async function add(item: LegalDocumentListItemDto) {
    const ids = [...members.map((m) => m.id), item.id];
    if (item.group && !(await askMove(item.title, item.group.title))) return;
    await run(
      async () => {
        try {
          return await setLegalGroupMembers(doc.id, ids, item.group !== null);
        } catch (err) {
          // Список устарел: документ уже попал в другую группу — спросить и повторить с переносом.
          if (legalErrorCode(err) !== "legal_in_group_already" || !(err instanceof ApiError)) throw err;
          const details = (err.details ?? null) as Partial<LegalInGroupAlreadyDetails> | null;
          if (!(await askMove(item.title, details?.groupTitle ?? "другая"))) return null;
          return setLegalGroupMembers(doc.id, ids, true);
        }
      },
      "Не удалось добавить документ",
      `«${item.title}» добавлен в группу`,
    );
  }

  async function remove(member: LegalGroupMemberDto) {
    if (isLastVisible(doc, member)) {
      const ok = await confirm({
        title: `Убрать «${member.title}» из группы?`,
        description: `Группа «${doc.title}» станет пустой и пропадёт с сайта.`,
        action: "Всё равно убрать",
        danger: true,
      });
      if (!ok) return;
    }
    await run(() => removeLegalGroupMember(doc, member.id), "Не удалось убрать документ", `«${member.title}» убран из группы — сам документ не удалён`);
  }

  if (doc.groupSource === "PRODUCER_CERTIFICATES") return <LegalCertificatesGroupBlock doc={doc} />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-on-surface">Документы в группе</span>
        <span className="type-caption text-on-surface-variant">Порядок здесь — порядок на сайте. Изменения сразу видны покупателям.</span>
      </div>
      <LegalGroupMembers
        members={members}
        onReorder={(ids) => void run(() => setLegalGroupMembers(doc.id, ids), "Не удалось изменить порядок")}
        onRemove={(m) => void remove(m)}
        disabled={busy}
      />
      <LegalAddMemberField groupId={doc.id} memberIds={members.map((m) => m.id)} onAdd={(item) => void add(item)} refreshKey={refreshKey} disabled={busy} />
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
